/**
 * Todo helpers for calendar integration
 * Converts todos with due dates to calendar event format
 */

import { Todo, KanbanColumn } from '../../hooks/useSupabaseKanban';
import { Calendar } from '../../types';
import { CalendarEvent } from '../../hooks/useSupabaseEvents';

/**
 * Extended CalendarEvent type for todos displayed on the calendar
 */
export interface TodoCalendarEvent extends Omit<CalendarEvent, 'id'> {
  id: string;
  isTodo: true;
  todoId: string;
  columnId: string;
  isCompleted: boolean;
}

/**
 * Type guard to check if an event is a todo
 */
export function isTodoEvent(event: CalendarEvent | TodoCalendarEvent): event is TodoCalendarEvent {
  return 'isTodo' in event && event.isTodo === true;
}

function addMinutesToTime(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(':').map(Number);
  const total = h * 60 + m + minutes;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * Convert todos with due dates to calendar events
 */
export function todosToCalendarEvents(
  todos: Todo[],
  columns: KanbanColumn[],
  calendars: Calendar[]
): TodoCalendarEvent[] {
  // Filter todos that have due dates and are not archived
  const todosWithDueDates = todos.filter(
    (todo) => todo.due_date && !todo.archived_at
  );

  return todosWithDueDates.map((todo) => {
    const column = columns.find((c) => c.id === todo.column_id);
    const tag = todo.calendar_id ? calendars.find((c) => c.id === todo.calendar_id) : null;
    const isCompleted = !!todo.completed_at;

    // Build color object similar to regular events
    const color = tag
      ? {
          bg: tag.color,
          name: tag.name,
        }
      : {
          bg: 'bg-unassigned',
          name: 'Todo',
        };

    return {
      id: `todo-${todo.id}`,
      isTodo: true as const,
      todoId: todo.id,
      columnId: todo.column_id,
      isCompleted,
      title: todo.title,
      notes: todo.notes || '',
      dates: [todo.due_date!], // We filtered for due_date above
      color,
      all_day: !todo.due_time, // All-day if no time specified
      start_time: todo.due_time,
      end_time: todo.due_time ? addMinutesToTime(todo.due_time, todo.duration ?? 30) : todo.due_time,
      space_id: todo.space_id,
      created_by: todo.created_by ?? undefined,
    };
  });
}

/**
 * Merge regular events with todos for calendar display
 * Returns combined array sorted by date
 */
export function mergeEventsWithTodos(
  events: CalendarEvent[],
  todoEvents: TodoCalendarEvent[]
): (CalendarEvent | TodoCalendarEvent)[] {
  return [...events, ...todoEvents];
}

/**
 * Filter todos that appear on a specific date
 */
export function getTodosForDate(
  todos: Todo[],
  dateKey: string
): Todo[] {
  return todos.filter(
    (todo) => todo.due_date === dateKey && !todo.archived_at
  );
}

/**
 * Get overdue todos (due date before today)
 */
export function getOverdueTodos(todos: Todo[]): Todo[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];

  return todos.filter((todo) => {
    if (!todo.due_date || todo.archived_at) return false;
    // Find the column to check if completed
    return todo.due_date < todayStr;
  });
}

/**
 * Get todos due today
 */
export function getTodayTodos(todos: Todo[]): Todo[] {
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  return todos.filter(
    (todo) => todo.due_date === todayStr && !todo.archived_at
  );
}

/**
 * Get upcoming todos (due in the next N days)
 */
export function getUpcomingTodos(todos: Todo[], days: number = 7): Todo[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const futureDate = new Date(today);
  futureDate.setDate(futureDate.getDate() + days);

  const todayStr = today.toISOString().split('T')[0];
  const futureStr = futureDate.toISOString().split('T')[0];

  return todos.filter((todo) => {
    if (!todo.due_date || todo.archived_at) return false;
    return todo.due_date > todayStr && todo.due_date <= futureStr;
  });
}
